import DeleteIcon from '@mui/icons-material/Delete';
import {
    TextField, MenuItem, Typography, Box, Button, Paper,
    Alert, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
    IconButton, Snackbar, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Tabs, Tab, Checkbox, FormControlLabel,
    List, ListItem, ListItemButton, ListItemText, LinearProgress,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { saveAs } from 'file-saver';
import EmailIcon from '@mui/icons-material/Email';
import J29 from '../../../Forms/J29';


// 🛠️ NOTE: TabPanel Helper Component
function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const AppointmentsTab = ({ matter, estateId }) => {
    // ... existing state declarations ...
    const [appointments, setAppointments] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [openEmailDialog, setOpenEmailDialog] = useState(false);
    const [outlookEmail, setOutlookEmail] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [user, setUser] = useState(null);

    // --- ADVERT STATE ---
    const [advertModalOpen, setAdvertModalOpen] = useState(false);
    const [advertModalItems, setAdvertModalItems] = useState([]);
    const [loadingAdvertItems, setLoadingAdvertItems] = useState(false);
    const [selectedAdvertItem, setSelectedAdvertItem] = useState(null);
    const [loadingForm, setLoadingForm] = useState(null);
    const [advertModalType, setAdvertModalType] = useState('form1');

    // Tab State: NOW 4 TABS (0 to 3)
    const [selectedTab, setSelectedTab] = useState(0);
    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };
    // ------------------------------------------

    const legalMatterId = matter?.id || '';
    const estateIdFromMatter = matter?.EstateID || estateId || '';

    // 🛠️ FIX: Find meetings by type more robustly.
    // This handles potential data inconsistencies like case differences (e.g., 'first' vs 'First')
    // and ensures we get the earliest meeting if multiple of the same type exist.
    const findMeeting = (type) => {
        const meetings = appointments.filter(app => app.type && app.type.toLowerCase() === type);
        meetings.sort((a, b) => new Date(a.proposedDateTime) - new Date(b.proposedDateTime));
        return meetings[0]; // Return the earliest meeting of that type, or undefined
    };
    const firstMeeting = findMeeting('first');

    // 🎯 Extract Master Data for initialization 🎯
    const masterOffice = matter?.mastersOffice || matter?.magistrateOffice || matter?.MasterOffice || '';
    const masterEstateNumber = matter?.estateNo || matter?.mastersReference || matter?.estateId || matter?.ID || ''; // 🔑 FIXED: 'estateNo' is checked first

    const formatDateForInput = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().split('T')[0]; // Returns "YYYY-MM-DD"
    };

    const [formData, setFormData] = useState({
        meetingName: '',
        magistrateOffice: masterOffice, 
        proposedDate: '',
        proposedTime: '',
        legalMatterId: legalMatterId,
        estateId: estateIdFromMatter,
        type: '',
        minutesReceived: false,

        // --- NEW FIELDS INITIALIZATION ---
        noticeLanguage: 'English',
        province: '',
        estateOrCompanyNumber: masterEstateNumber, 
        estateOrCompanyStatus: '',
        estateOrCompanyName: '',
        idOrRegNumber: '',
        purposeOfMeeting: '',
        annexureOrAdditionalInfo: '',
        debtPaymentPeriod: '',
        advertiserName: '',
        advertiserAddress: '',
        advertiserEmail: '',
        advertiserTelephone: '',
        dateSubmitted: formatDateForInput(new Date()),
        publicationDate: '',
        // --- END NEW FIELDS ---
    });

    // Array of full hour options (e.g., "09:00", "14:00")
    const hourOptions = Array.from({ length: 24 }, (_, hour) =>
        `${String(hour).padStart(2, '0')}:00`
    );

    // --- START OMITTED EXISTING FUNCTIONS ---

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const res = await axios.get('https://ipms.insollex.com/api/user', {
                    withCredentials: true,
                });

                if (typeof res.data !== 'object' || res.data === null) {
                    console.error('Invalid user data received:', res.data);
                    setUser(null);
                    return;
                }

                if (!res.data.role) {
                    console.warn('User has no role defined. Treating as guest.');
                    res.data.role = 'guest';
                }

                setUser(res.data);
            } catch (err) {
                console.error('Could not fetch user', err);
                setUser(null);
            }
        };

        fetchUser();
    }, []);

    const formatDate = (date) => {
        if (!date) return { date: '', time: '' };

        const formattedDate = new Date(date);
        if (isNaN(formattedDate.getTime())) return { date: '', time: '' };

        const datePart = formattedDate.toISOString().split('T')[0];
        // 🛠️ Updated: Extract just the hour and format as HH:00
        const timePart = formattedDate.toTimeString().slice(0, 2) + ':00';

        return { date: datePart, time: timePart };
    };

    // Fetch appointments
    const fetchAppointments = async () => {
        try {
            let response;
            if (legalMatterId) {
                response = await axios.get(
                    `https://ipms.insollex.com/api/appointments?legalMatterId=${legalMatterId}`
                );
            } else if (estateIdFromMatter) {
                // response = await axios.get(
                //     `https://ipms.insollex.com/api/old-database/meetings/${estateIdFromMatter}`
                // );
            } else {
                console.warn('No legalMatterId or estateId provided. Skipping fetch.');
                return;
            }

            const data = response?.data?.data || response?.data || [];
            setAppointments(data);
        } catch (error) {
            console.error('Error fetching appointments:', error);
            setSnackbarMessage('Error fetching appointments.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    useEffect(() => {
        if (!legalMatterId && !estateIdFromMatter) return;
        fetchAppointments();
    }, [legalMatterId, estateIdFromMatter]);

    // Handle form field changes
    const handleFormChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    // Add new meeting
    const handleAddMeeting = () => {
        setIsEditing(false);
        setFormData({
            meetingName: '',
            magistrateOffice: masterOffice, // 🎯 RESET/INIT FROM MATTER 🎯
            estateOrCompanyNumber: masterEstateNumber, // 🎯 RESET/INIT FROM MATTER 🎯
            proposedDate: '',
            proposedTime: '09:00', // Default to 9 AM
            legalMatterId,
            estateId: estateIdFromMatter,
            type: '',
            minutesReceived: false,

            // --- NEW FIELDS RESET ---
            noticeLanguage: 'English',
            province: '',
            estateOrCompanyStatus: '',
            estateOrCompanyName: '',
            idOrRegNumber: '',
            purposeOfMeeting: '',
            annexureOrAdditionalInfo: '',
            debtPaymentPeriod: '',
            advertiserName: '',
            advertiserAddress: '',
            advertiserEmail: '',
            advertiserTelephone: '',
            dateSubmitted: formatDateForInput(new Date()),
            publicationDate: '',
            // --- END NEW FIELDS RESET ---
        });
        setOpenDialog(true);
    };

    // Edit meeting
    const handleEditMeeting = (appointment) => {
    setIsEditing(true);
    setSelectedAppointment(appointment);

    // 1. Safely parse the date
    const proposed = appointment.proposedDateTime ? new Date(appointment.proposedDateTime) : null;
    
    // Check if the date is valid (not NaN)
    const isValidDate = proposed && !isNaN(proposed.getTime());

    // 2. Determine safe fallbacks for Date and Time
    const proposedDate = isValidDate ? proposed.toISOString().split('T')[0] : '';
    const proposedTime = isValidDate ? proposed.toTimeString().slice(0, 2) + ':00' : '09:00';

    setFormData({
        meetingName: appointment.meetingName || '',
        
        // 🎯 LOAD FROM APPOINTMENT OR FALLBACK TO MATTER 🎯
        magistrateOffice: appointment.magistrateOffice || masterOffice,
        estateOrCompanyNumber: appointment.estateOrCompanyNumber || masterEstateNumber,

        proposedDate: proposedDate, // Safe version
        proposedTime: proposedTime, // Safe version
        
        legalMatterId: appointment.legalMatterId || '',
        estateId: appointment.estateId || '',
        type: appointment.type || '',
        minutesReceived: !!appointment.minutesReceived,

        // --- NEW FIELDS LOAD FROM APPOINTMENT ---
        noticeLanguage: appointment.noticeLanguage || 'English',
        province: appointment.province || '',
        placeOfMeeting: appointment.placeOfMeeting || '',
        estateOrCompanyStatus: appointment.estateOrCompanyStatus || '',
        estateOrCompanyName: appointment.estateOrCompanyName || '',
        idOrRegNumber: appointment.idOrRegNumber || '',
        purposeOfMeeting: appointment.purposeOfMeeting || '',
        annexureOrAdditionalInfo: appointment.annexureOrAdditionalInfo || '',
        debtPaymentPeriod: appointment.debtPaymentPeriod || '',
        advertiserName: appointment.advertiserName || '',
        advertiserAddress: appointment.advertiserAddress || '',
        advertiserEmail: appointment.advertiserEmail || '',
        advertiserTelephone: appointment.advertiserTelephone || '',
        
        // Use helper for extra safety on these date fields
        dateSubmitted: formatDateForInput(appointment.dateSubmitted || new Date()),
        publicationDate: formatDateForInput(appointment.publicationDate),
        // --- END NEW FIELDS LOAD ---
    });

    setOpenDialog(true);
};

    // Save meeting (add or update)
    const handleSaveMeeting = async () => {
    // 1. Client-side Validation
    if (!formData.proposedDate || !formData.type || !formData.meetingName) {
        setSnackbarMessage('Please fill in required fields: Meeting Name, Type, and Date.');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        return;
    }

    try {
        // 2. Format Dates for the Backend (ISO 8601)
        // We use a local date string to avoid timezone shifts if the user is in SAST
        const dateString = `${formData.proposedDate}T${formData.proposedTime || '09:00'}:00`;
        const proposedDateTime = new Date(dateString).toISOString();
        
        // Using proposedDateTime as a fallback for dueDateTime as requested
        const dueDateTime = proposedDateTime;

        // 3. Prepare Payload
        const dataToSave = {
            ...formData, // Spreads all 15+ new fields automatically
            proposedDateTime,
            dueDateTime,
            // Ensure legalMatterId is a string for UUID compatibility
            legalMatterId: formData.legalMatterId ? String(formData.legalMatterId) : null,
        };

        // 4. API Call
        if (isEditing) {
            await axios.put(
                `https://ipms.insollex.com/api/appointments/${selectedAppointment.id}`,
                dataToSave
            );
            setSnackbarMessage('Appointment updated successfully!');
        } else {
            const res = await axios.post('https://ipms.insollex.com/api/appointments', dataToSave);
            
            // Immediately update the local state with the returned object (including its new ID)
            setAppointments((prev) => [...prev, res.data]);
            setSnackbarMessage('Appointment added successfully!');
        }

        // 5. Cleanup & Refresh
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setOpenDialog(false);
        fetchAppointments(); // Refresh the list to ensure data integrity

    } catch (error) {
        console.error('Save Error:', error);
        
        // Extract specific error message if backend sent one
        const errorMsg = error.response?.data?.message || 'Error saving appointment.';
        
        setSnackbarMessage(errorMsg);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
    }
};

    const handleEmailChange = (e) => {
        setOutlookEmail(e.target.value);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleDeleteAppointment = async (id) => {
        try {
            await axios.delete(`https://ipms.insollex.com/api/appointments/${id}`);
            setAppointments((prev) => prev.filter((a) => a.id !== id));
            setSnackbarMessage('Appointment deleted successfully!');
            setSnackbarSeverity('success');
        } catch {
            setSnackbarMessage('Error deleting appointment.');
            setSnackbarSeverity('error');
        } finally {
            setSnackbarOpen(true);
        }
    };

    const handleSendToOutlook = () => {
        setOpenEmailDialog(true);
    };

    const handleSubmitEmail = async () => {
        if (!outlookEmail || !outlookEmail.includes('@')) {
            setSnackbarMessage('Please enter a valid email address.');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
        }

        const meetings = appointments
            .map((a) => {
                const start = new Date(a.proposedDateTime);
                const end = new Date(a.dueDateTime);
                if (isNaN(start) || isNaN(end)) return null;

                return {
                    subject: `Meeting – ${a.meetingName}`,
                    description: `Scheduled meeting regarding ${a.meetingName} at ${a.magistrateOffice}.`,
                    location: a.magistrateOffice || 'Online',
                    startTime: [
                        start.getFullYear(),
                        start.getMonth() + 1,
                        start.getDate(),
                        start.getHours(),
                        start.getMinutes(),
                    ],
                    endTime: [
                        end.getFullYear(),
                        end.getMonth() + 1,
                        end.getDate(),
                        end.getHours(),
                        end.getMinutes(),
                    ],
                };
            })
            .filter(Boolean);

        if (!meetings.length) {
            setSnackbarMessage('No valid appointments with dates found.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        try {
            await axios.post('https://ipms.insollex.com/api/send-outlook-meeting', {
                email: outlookEmail,
                meetings,
            });

            setSnackbarMessage('All appointments sent to Outlook successfully!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setOpenEmailDialog(false);
        } catch (err) {
            console.error('Failed to send appointments:', err.response?.data || err);
            const errorMessage =
                err?.response?.data?.message || 'Failed to send Outlook invites.';
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleToggleMinutes = async (appointmentId, value) => {
        try {
            await axios.put(`https://ipms.insollex.com/api/appointments/${appointmentId}`, {
                minutesReceived: value,
            });
            setAppointments((prev) =>
                prev.map((a) => (a.id === appointmentId ? { ...a, minutesReceived: value } : a))
            );
        } catch (error) {
            console.error('Error updating minutes received:', error);
        }
    };

    const handleCloseEmailDialog = () => {
        setOpenEmailDialog(false);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const meetingTypeMap = {
        first: 'First Meeting',
        second: 'Second Meeting',
        special: 'Special Meeting',
        specialGeneral: 'Special General Meeting',
    };

    // --- ADVERT LOGIC START (MEETING ADVERT ONLY) ---

    const handleOpenAdvertModal = async (formType) => {
        setAdvertModalType(formType || 'j29');
        setAdvertModalItems([]);
        setSelectedAdvertItem(null);
        setAdvertModalOpen(false);

        setLoadingAdvertItems(true);
        setAdvertModalOpen(true);

        try {
            const url = legalMatterId
                ? `https://ipms.insollex.com/api/appointments?legalMatterId=${legalMatterId}`
                : ""; // `https://ipms.insollex.com/api/old-database/meetings/${estateIdFromMatter}`;

            if (!url) throw new Error("Old database API is commented out");
            const response = await axios.get(url);
            const items = (response.data.data || response.data || []).map((item) => {
                const date = item.proposedDateTime ? new Date(item.proposedDateTime) : null;
                return {
                    id: item.id || item.ID,
                    type: item.type || 'meeting',
                    name: `${meetingTypeMap[item.type] || 'Meeting'} - ${
                        date ? date.toLocaleDateString() : 'No Date'
                    }`,
                };
            });

            setAdvertModalItems(items);
        } catch (error) {
            console.error('Error fetching data for advert:', error);
            setSnackbarMessage('Error fetching data for notice.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setAdvertModalOpen(false);
        } finally {
            setLoadingAdvertItems(false);
        }
    };

    const handleSelectAdvertItem = (item, formName) => {
        setSelectedAdvertItem(item);
        handleGenerateAdvertPdf(item, formName);
    };

    const handleCloseAdvertModal = () => {
        setAdvertModalOpen(false);
        setSelectedAdvertItem(null);
    };

    // NOTE: Updated the template map to reflect the new structure.
    const handleGenerateAdvertPdf = async (selection, formName) => {
        let targetFormName = formName;
        const targetTemplateMap = {
            form1: ['Meeting Advert.docx'],
            j29: ['Form_J29_CC.docx'],
            form2: ['Form 2 Second Meeting Notice.docx'],
        };

        const templateFiles = targetTemplateMap[targetFormName];

        if (!legalMatterId && !estateIdFromMatter) {
            setSnackbarMessage('Legal Matter or Estate not found');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            handleCloseAdvertModal();
            return;
        }

        if (!templateFiles || templateFiles.length === 0) {
            setSnackbarMessage(`No template defined for ${targetFormName.toUpperCase()}.`);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            handleCloseAdvertModal();
            return;
        }


        setLoadingForm(targetFormName);
        handleCloseAdvertModal();

        try {
            const BASE_URL = 'https://ipms.insollex.com';

            let selectionParam = selection?.id || '';

            for (const templateFileName of templateFiles) {
                let idToUse;
                let pathType;

                if (legalMatterId) {
                    idToUse = legalMatterId;
                    pathType = 'legalMatter';
                } else if (estateIdFromMatter) {
                    idToUse = estateIdFromMatter;
                    pathType = 'estate';
                } else {
                    continue;
                }

                const url = `${BASE_URL}/api/pdf/${pathType}/${idToUse}?templateName=${encodeURIComponent(
                    templateFileName
                )}&selection=${encodeURIComponent(selectionParam)}`;

                try {
                    const response = await axios.get(url, { responseType: 'blob' });
                    const blob = new Blob([response.data], { type: 'application/pdf' });

                    const cleanName = templateFileName.replace('.docx', '').replace(/\s+/g, '_');
                    const fileName = `${targetFormName}_${idToUse}_${cleanName}_${selectionParam}.pdf`;

                    saveAs(blob, fileName);

                    setSnackbarMessage(`PDF generated successfully: ${fileName}`);
                    setSnackbarSeverity('success');
                    setSnackbarOpen(true);
                } catch (downloadError) {
                    console.error(`[PDF] Error downloading template ${templateFileName}:`, downloadError);
                    setSnackbarMessage(
                        `Failed to download template ${templateFileName}: ${
                            downloadError?.response?.data?.message ||
                            downloadError?.message ||
                            'Server error'
                        }`
                    );
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                }
            }
        } catch (err) {
            console.error('[PDF] Unexpected error:', err);
            setSnackbarMessage(
                `Unexpected error generating ${targetFormName.toUpperCase()}: ${err?.message || err}`
            );
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoadingForm(null);
        }
    };

    // --- ADVERT LOGIC END ---

    return (
        // Outer Box maintains full width
        <Box sx={{ width: '100%' }}>

            {/* Tab Header (Centered) */}
            <Grid item xs={12}>
                <Box
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex', 
                        justifyContent: 'center'
                    }}
                >
                    <Tabs value={selectedTab} onChange={handleTabChange} aria-label="Insolvency Forms and Meetings tabs" variant="scrollable" scrollButtons="auto">
                        <Tab label="Meetings" /> {/* Index 0 */}
                        <Tab label="Form J29" /> {/* Index 1 (J29 content moved here) */}
                        <Tab label="Form 1" /> {/* Index 2 (Form 01 Statutory content moved here) */}
                        <Tab label="Form 2" /> {/* Index 3 */}
                    </Tabs>
                </Box>
            </Grid>

            {/* Tab Panel 1: Meetings Table and Action Buttons (Index 0) */}
            <TabPanel value={selectedTab} index={0}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-end" gap={2} mb={1}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleAddMeeting}
                                disabled={user?.role === 'guest' || user?.isInvited}
                            >
                                Schedule Meeting
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                size="large"
                                onClick={handleSendToOutlook}
                                disabled={user?.role === 'guest' || user?.isInvited}
                            >
                                Send Meetings to my Outlook
                            </Button>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mb: 1 }} align="center">
                            Scheduled Meetings
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Meeting Type</strong></TableCell>
                                        <TableCell><strong>Meeting Name</strong></TableCell>
                                        <TableCell><strong>Masters/Magistrate Office</strong></TableCell>
                                        <TableCell><strong>Schedule Date & Time</strong></TableCell>
                                        <TableCell><strong>Minutes Received</strong></TableCell>
                                        <TableCell><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {appointments.map((appointment) => {
                                        const proposedDateTime = appointment.proposedDateTime ? new Date(appointment.proposedDateTime) : null;
                                        
                                        const timeOptions = { hour: '2-digit', minute: '2-digit' };
                                        
                                        const proposedStr = proposedDateTime && !isNaN(proposedDateTime.getTime())
                                            ? `${proposedDateTime.toLocaleDateString()} ${proposedDateTime.toLocaleTimeString([], timeOptions).replace(/:\d{2}\s/g, ' ')}`
                                            : '';
                                            
                                        return (
                                            <TableRow key={appointment.id}>
                                                <TableCell>{meetingTypeMap[appointment.type] || appointment.type}</TableCell>
                                                <TableCell>{appointment.meetingName}</TableCell>
                                                <TableCell>{appointment.magistrateOffice}</TableCell>
                                                <TableCell>{proposedStr}</TableCell>
                                                <TableCell style={{ paddingLeft: 0, paddingRight: 8 }} align="left">
                                                    {appointment.minutesReceived ? (
                                                        <span style={{ color: 'green', fontSize: '1.2rem' }}>✔️</span>
                                                    ) : (
                                                        <span style={{ color: 'red', fontSize: '1.2rem' }}>❌</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Button color="primary" onClick={() => handleEditMeeting(appointment)}>Edit</Button>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleDeleteAppointment(appointment.id)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </TabPanel>

            {/* Tab Panel 2: Form J29 (Index 1) */}
            <TabPanel value={selectedTab} index={1}>
                <J29 
                    matter={matter} 
                    appointment={firstMeeting} 
                    isPreview={false} 
                    onSaveSuccess={fetchAppointments} 
                />
            </TabPanel>

            {/* 🛠️ Tab Panel 3: Form 1 (Index 2) - Under Construction */}
            <TabPanel value={selectedTab} index={2}>
                <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, textAlign: 'center' }}>
                    <Typography variant="h4" color="text.secondary" gutterBottom>
                        Form 1 is Under Construction 🚧
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        The functionality for the statutory form 1 will be available soon.
                    </Typography>
                </Box>
            </TabPanel>

            {/* 🛠️ Tab Panel 4: Form 2 (Index 3) - Under Construction */}
            <TabPanel value={selectedTab} index={3}>
                <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, textAlign: 'center' }}>
                    <Typography variant="h4" color="text.secondary" gutterBottom>
                        Form 2 is Under Construction 🚧
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        The functionality for the statutory form 2 will be available soon.
                    </Typography>
                </Box>
            </TabPanel>
            
            {/* Add/Edit Meeting Dialog */}
<Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
    <DialogTitle>{isEditing ? 'Edit Meeting' : 'Schedule New Meeting'}</DialogTitle>
    <DialogContent>
        {/* Meeting Type (Existing Field) */}
        <TextField
            select
            label="Meeting Type"
            fullWidth
            margin="normal"
            name="type"
            value={formData.type}
            onChange={handleFormChange}
        >
            <MenuItem value="first">First Meeting</MenuItem>
            <MenuItem value="second">Second Meeting</MenuItem>
            <MenuItem value="special">Special Meeting</MenuItem>
            <MenuItem value="specialGeneral">Special General Meeting</MenuItem>
        </TextField>

        {/* 🛠️ MANDATORY FIELDS 🛠️ */}
        <Grid container spacing={2}>
            {/* Notice Language */}
            <Grid item xs={6}>
                <TextField
                    select
                    label="*Notice Language / Taal van Kennisgewing"
                    fullWidth
                    margin="normal"
                    name="noticeLanguage"
                    value={formData.noticeLanguage || ''}
                    onChange={handleFormChange}
                >
                    <MenuItem value="English">English</MenuItem>
                    <MenuItem value="Afrikaans">Afrikaans</MenuItem>
                </TextField>
            </Grid>
            {/* Province */}
            <Grid item xs={6}>
                <TextField
                    label="*Province / Provinsie"
                    fullWidth
                    margin="normal"
                    name="province"
                    value={formData.province || ''}
                    onChange={handleFormChange}
                />
            </Grid>
        </Grid>

        {/* Estate Number or Company Number - PULLS MASTER DATA */}
        <TextField
            label="*Estate Number or Company Number / Boedelnommer of Maatskappynommer"
            fullWidth
            margin="normal"
            name="estateOrCompanyNumber"
            // FIX: Use formData state value (initialized from master data)
            value={formData.estateOrCompanyNumber} 
            disabled={true} 
        />

        {/* Insolvent Estate or Company In Liquidation (Status) */}
        <TextField
            label="Insolvent Estate or Company In Liquidation / Insolvente Boedel of Maatskappy In Likwidasie"
            fullWidth
            margin="normal"
            name="estateOrCompanyStatus"
            value={formData.estateOrCompanyStatus || ''}
            onChange={handleFormChange}
        />

        {/* Full Name of Estate or Company */}
        <TextField
            label="*Full Name of Estate or Company / Volle Naam van Boedel of Maatskappy"
            fullWidth
            margin="normal"
            name="estateOrCompanyName"
            value={formData.estateOrCompanyName || ''}
            onChange={handleFormChange}
        />

        {/* ID Number(s) or Company Registration Number */}
        <TextField
            label="ID Number(s) or Company Registration Number / Identiteitsnommer(s) of Registrasienommer van Maatskappy"
            fullWidth
            margin="normal"
            name="idOrRegNumber"
            value={formData.idOrRegNumber || ''}
            onChange={handleFormChange}
        />

        {/* Meeting Name/Description */}
        <TextField
            label="Meeting Name/Description"
            fullWidth
            margin="normal"
            name="meetingName"
            value={formData.meetingName}
            onChange={handleFormChange}
        />

        {/* Masters/Magistrate Office - PULLS MASTER DATA */}
        <TextField
            label="Masters/Magistrate Office"
            fullWidth
            margin="normal"
            name="magistrateOffice"
            // FIX: Use formData state value (initialized from master data)
            value={formData.magistrateOffice} 
            disabled={true} 
        />

        {/* Scheduled Date/Time (Existing Fields) */}
        <Grid container spacing={2}>
            <Grid item xs={6}>
                <TextField
                    label="*Date of Meeting / Datum van Byeenkoms (CCYY-MM-DD)"
                    type="date"
                    fullWidth
                    margin="normal"
                    name="proposedDate"
                    value={formData.proposedDate}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={6}>
                {/* 🛠️ Existing Field: Select for hour-only time */}
                <TextField
                    select
                    label="*Time of Meeting / Tyd van Byeenkoms (HH:MM)"
                    fullWidth
                    margin="normal"
                    name="proposedTime"
                    value={formData.proposedTime}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                >
                    {hourOptions.map(hour => (
                        <MenuItem key={hour} value={hour}>
                            {hour}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
        </Grid>

        {/* Place of Meeting */}
        <TextField
            label="*Place of Meeting / Plek van Byeenkoms"
            fullWidth
            margin="normal"
            name="placeOfMeeting"
            value={formData.placeOfMeeting || ''}
            onChange={handleFormChange}
        />

        {/* Purpose of Meeting */}
        <TextField
            label="*Purpose of Meeting / Doel van Byeenkoms"
            fullWidth
            margin="normal"
            name="purposeOfMeeting"
            value={formData.purposeOfMeeting || ''}
            onChange={handleFormChange}
        />

        {/* Annexure / Additional Information */}
        <TextField
            label="Annexure or additional information / Vir 'n Aanhangsel of addisionele inligting"
            fullWidth
            multiline
            rows={3}
            margin="normal"
            name="annexureOrAdditionalInfo"
            value={formData.annexureOrAdditionalInfo || ''}
            onChange={handleFormChange}
        />

        {/* Period within which debt must be paid */}
        <TextField
            label="Period within which debt must be paid if not to be done forthwith"
            fullWidth
            margin="normal"
            name="debtPaymentPeriod"
            value={formData.debtPaymentPeriod || ''}
            onChange={handleFormChange}
        />

        {/* 🛠️ SECTION: Advertiser Information (Unchanged) 🛠️ */}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Advertiser Information</Typography>

        <Grid container spacing={2}>
            <Grid item xs={6}>
                {/* Advertiser Name */}
                <TextField
                    label="*Advertiser Name"
                    fullWidth
                    margin="normal"
                    name="advertiserName"
                    value={formData.advertiserName || ''}
                    onChange={handleFormChange}
                />
            </Grid>
            <Grid item xs={6}>
                {/* Advertiser Telephone */}
                <TextField
                    label="*Advertiser Telephone"
                    fullWidth
                    margin="normal"
                    name="advertiserTelephone"
                    value={formData.advertiserTelephone || ''}
                    onChange={handleFormChange}
                />
            </Grid>
        </Grid>

        {/* Advertiser Address */}
        <TextField
            label="Advertiser Address"
            fullWidth
            margin="normal"
            name="advertiserAddress"
            value={formData.advertiserAddress || ''}
            onChange={handleFormChange}
        />

        <Grid container spacing={2}>
            <Grid item xs={6}>
                {/* Advertiser Email */}
                <TextField
                    label="Advertiser Email"
                    fullWidth
                    margin="normal"
                    name="advertiserEmail"
                    type="email"
                    value={formData.advertiserEmail || ''}
                    onChange={handleFormChange}
                />
            </Grid>
            <Grid item xs={6}>
                {/* Date Submitted */}
                <TextField
                    label="*Date Submitted"
                    type="date"
                    fullWidth
                    margin="normal"
                    name="dateSubmitted"
                    value={formData.dateSubmitted || ''}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>
        </Grid>

        {/* For Publication in the Government Gazette on */}
        <TextField
            label="*For Publication in the Government Gazette on (CCYY-MM-DD)"
            type="date"
            fullWidth
            margin="normal"
            name="publicationDate"
            value={formData.publicationDate || ''}
            onChange={handleFormChange}
            InputLabelProps={{ shrink: true }}
        />

        {/* Minutes Received (Existing Field) */}
        <FormControlLabel
            control={
                <Checkbox
                    checked={formData.minutesReceived}
                    onChange={handleFormChange}
                    name="minutesReceived"
                    color="primary"
                />
            }
            label="Minutes Received"
        />
    </DialogContent>
    <DialogActions>
        <Button onClick={handleCloseDialog} color="error">
            Cancel
        </Button>
        <Button onClick={handleSaveMeeting} color="primary" variant="contained">
            {isEditing ? 'Save Changes' : 'Add Meeting'}
        </Button>
    </DialogActions>
</Dialog>
            
            {/* Send to Outlook Email Dialog (Unchanged) */}
            <Dialog open={openEmailDialog} onClose={handleCloseEmailDialog} fullWidth maxWidth="xs">
                <DialogTitle>Send Meetings to Outlook Calendar</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Enter your email address to receive .ics files for all scheduled meetings.
                    </Typography>
                    <TextField
                        label="Outlook Email Address"
                        type="email"
                        fullWidth
                        margin="dense"
                        value={outlookEmail}
                        onChange={handleEmailChange}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSubmitEmail} color="primary" variant="contained">
                        Send
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Advert Item Selection Modal (Unchanged) */}
            <Dialog open={advertModalOpen} onClose={handleCloseAdvertModal} fullWidth maxWidth="sm">
                <DialogTitle>
                    Select Meeting for {advertModalType === 'j29' ? 'Form J29 Notice' : 'Form 2 Notice'}
                </DialogTitle>
                <DialogContent>
                    {loadingAdvertItems ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                            <LinearProgress sx={{ width: '80%' }} />
                        </Box>
                    ) : advertModalItems.length === 0 ? (
                        <Alert severity="warning">No meetings found to generate the notice.</Alert>
                    ) : (
                        <List component={Paper} elevation={1} sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {advertModalItems.map((item) => (
                                <ListItem key={item.id} disablePadding>
                                    <ListItemButton onClick={() => handleSelectAdvertItem(item, advertModalType)}>
                                        <ListItemText primary={item.name} secondary={`Type: ${item.type}`} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAdvertModal} color="error">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for Notifications (Unchanged) */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

        </Box>
    );
};

export default AppointmentsTab;